import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, FileText, CalendarCheck, Flame } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, ago } from "@/lib/api";

const KIND = {
  brochure: { icon: FileText, cls: "text-blue-600 bg-blue-50", text: "opened the brochure" },
  booking: { icon: CalendarCheck, cls: "text-emerald-600 bg-emerald-50", text: "booked a consultation" },
  both: { icon: Flame, cls: "text-orange-600 bg-orange-50", text: "opened the brochure AND booked. Very hot lead" },
};
const who = (a) => `${a.company_name || "A lead"}${a.contact ? ` (${a.contact})` : ""}`;

export function AlertBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const since = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/alerts", { params: since.current ? { since: since.current } : {} });
        if (!alive) return;
        if (since.current) {
          data.items.slice().reverse().forEach((a) => {
            const k = KIND[a.kind] || KIND.brochure;
            toast[a.kind === "both" ? "warning" : "success"](`${who(a)} ${k.text}`, { duration: 10000, action: { label: "Open lead", onClick: () => nav(`/leads/${a.lead_id}`) } });
          });
          setItems((p) => [...data.items, ...p].slice(0, 50));
        } else setItems(data.items);
        setUnread(data.unread);
        since.current = data.items[0]?.at && data.items[0].at > (since.current || "") ? data.items[0].at : since.current || data.server_time;
      } catch { /* ignore polling errors */ }
    };
    poll(); const t = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [nav]);
  const onOpen = (o) => { setOpen(o); if (o && unread) api.post("/alerts/seen").then(() => setUnread(0)); };
  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-slate-800 hover:text-white" data-testid="alerts-bell">
          <span className="relative"><Bell size={17} />{unread > 0 && <span className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold grid place-items-center" data-testid="alerts-unread">{unread}</span>}</span>
          <span className="hidden lg:inline">Hot lead alerts</span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="w-96 p-0" data-testid="alerts-panel">
        <div className="px-4 py-3 border-b font-bold text-sm">Hot lead alerts</div>
        <div className="max-h-96 overflow-y-auto">
          {items.map((a) => { const k = KIND[a.kind] || KIND.brochure; const I = k.icon; return (
            <button key={a.id} onClick={() => { setOpen(false); nav(`/leads/${a.lead_id}`); }} className="w-full text-left flex gap-3 px-4 py-3 border-b last:border-0 hover:bg-slate-50" data-testid={`alert-item-${a.id}`}>
              <span className={`h-8 w-8 shrink-0 rounded-lg grid place-items-center ${k.cls}`}><I size={15} /></span>
              <span className="text-sm"><b>{who(a)}</b> {k.text}<span className="block text-xs text-slate-400">{ago(a.at)}</span></span>
            </button>); })}
          {!items.length && <div className="p-6 text-center text-sm text-slate-500" data-testid="alerts-empty">No hot lead activity yet. Brochure opens and bookings will show here.</div>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
